import { PackagePlus, ScrollText } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { Department, ReagentItemStock } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { DensityControl } from '#client/components/ui/DensityControl';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { Pagination } from '#client/components/ui/Pagination';
import { SearchFilter } from '#client/components/ui/SearchFilter';
import { Select } from '#client/components/ui/Select';
import { FilterChip } from '#client/components/ui/Tabs';
import { useCan } from '#client/hooks/useCan';
import { useUrlState } from '#client/hooks/useUrlState';
import { RESOURCE, routes } from '#client/lib/routes';
import { nameOptions } from '#client/lib/form-choices';
import { reagentStockColumns } from './inventory-columns';
import { countOf } from '#client/lib/format';
import { useAllRecords } from '#client/hooks/useAllRecords';
import { useResourceList } from '#client/hooks/useResourceList';

/**
 * The lab's "Inventory Detail" sheet, corrected.
 *
 * Their spreadsheet has one row per *parameter* and a stored "Notification"
 * column. Both are wrong in the same way: one kit feeding three tests is
 * counted three times, and a notice written into a cell says "Please reorder the
 * Hep C kit" for a fortnight after the kit arrives. Here a row is one purchased
 * *item*, and every derived figure — on hand, runs left, the notice — is
 * computed on read.
 */
export function InventoryPage() {
  const navigate = useNavigate();
  const can = useCan();
  const { params, setParam, toggleParam } = useUrlState();

  const departmentId = params.get('department');
  const lowStock = params.get('low_stock') === 'true';

  const departments = useAllRecords<Department>({ path: RESOURCE.departments });

  const extraQuery = useMemo(
    () => ({
      ...(departmentId ? { department_id: departmentId } : {}),
      ...(lowStock ? { low_stock: 'true' } : {}),
    }),
    [departmentId, lowStock],
  );

  const items = useResourceList<ReagentItemStock>({ path: RESOURCE.reagentItems, extraQuery });

  return (
    <Page
      title="Inventory"
      subtitle="What is on the shelf, and what needs reordering"
      fullWidth
      secondaryActions={
        can('stock.view') ? (
          <button
            type="button"
            className="c-button c-button--secondary c-button--md"
            onClick={() => void navigate(routes.stockLedger)}
          >
            <ScrollText aria-hidden />
            Stock ledger
          </button>
        ) : undefined
      }
      primaryAction={
        can('lot.create') ? (
          <button
            type="button"
            className="c-button c-button--primary c-button--md"
            onClick={() => void navigate(routes.stockEntry)}
          >
            <PackagePlus aria-hidden />
            Enter stock
          </button>
        ) : undefined
      }
    >
      <Card flush>
        <InventoryToolbar
          search={items.search}
          onSearchChange={items.setSearch}
          count={items.count}
          departments={departments.items}
          departmentId={departmentId}
          onDepartmentChange={(choice) => setParam('department', choice || undefined)}
          lowStock={lowStock}
          onLowStockToggle={() => toggleParam('low_stock')}
        />

        <DataTable
          columns={reagentStockColumns}
          rows={items.items}
          rowKey={(row) => row.id}
          loading={items.loading}
          caption="Reagents and their stock levels"
          onRowClick={(row) => void navigate(routes.reagentItem(row.id))}
          emptyState={<InventoryEmptyState lowStock={lowStock} />}
        />

        <Pagination
          page={items.page}
          limit={items.limit}
          count={items.count}
          onPageChange={items.setPage}
          label="reagents"
        />
      </Card>
    </Page>
  );
}

function InventoryEmptyState({ lowStock }: { lowStock: boolean }) {
  return lowStock ? (
    <EmptyState
      title="Nothing needs reordering"
      body="Every reagent is above its reorder level."
      tone="positive"
    />
  ) : (
    <EmptyState
      title="No reagents yet"
      body="Add the kits and bottles the lab buys, then enter what is on the shelf."
      tone="neutral"
    />
  );
}

interface InventoryToolbarProps {
  search: string;
  onSearchChange: (search: string) => void;
  count: number;
  departments: readonly Department[];
  departmentId: string | null;
  onDepartmentChange: (departmentId: string) => void;
  lowStock: boolean;
  onLowStockToggle: () => void;
}

function InventoryToolbar({
  search,
  onSearchChange,
  count,
  departments,
  departmentId,
  onDepartmentChange,
  lowStock,
  onLowStockToggle,
}: InventoryToolbarProps) {
  return (
    <div className="c-table-toolbar">
      <div className="c-table-toolbar__search">
        <SearchFilter
          value={search}
          onChange={onSearchChange}
          placeholder="Search by reagent, code or barcode"
        />
      </div>
      <Select
        label="Department"
        hideLabel
        options={nameOptions(departments)}
        placeholder="All departments"
        value={departmentId ?? ''}
        onChange={onDepartmentChange}
      />
      <FilterChip active={lowStock} onClick={onLowStockToggle}>
        Needs reordering
      </FilterChip>
      <span className="c-table-toolbar__count">{countOf(count, 'reagent')}</span>
      <DensityControl />
    </div>
  );
}
